import { DataSourcePropertyValueChange } from "../../Data/DataSourcePropertyValueChange";
import { PartialNullable } from "../../Extensions";
import { IJNativeEvent } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyObject, PropertyString } from "../../RadObject";
import { ClientAwareCategoryAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareCategoryAttribute";
import { ClientAwareDisplayNameAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareDisplayNameAttribute";
import { BuiltInTypes } from "../../Server/DotNet/System/BuiltInTypes";
import { IContainerControl } from "./ContainerControl";
import { Control, ControlPropertyValues, IControl } from "./Control";
import { NAMESPACE } from "./_Namespace";

export type PropertyBoundControlPropertyValues =
    ControlPropertyValues
    & {
        dataSourceProperty: string;
        dataSourcePropertyType: BuiltInTypes;
        value: any;
    };

export type PropertyBoundControlProperties = {
    [name in keyof PropertyBoundControlPropertyValues]: Property<PropertyBoundControlPropertyValues[name]>
};

export interface IPropertyBoundControl extends IControl, PropertyBoundControlProperties
{
}

export abstract class PropertyBoundControl<TPropertyValues extends PropertyBoundControlPropertyValues = PropertyBoundControlPropertyValues> extends Control<TPropertyValues> implements IPropertyBoundControl
{
    public static override classNamespace = NAMESPACE + "PropertyBoundControl";
    public static override className = "PropertyBoundControl";

    // #region Global Variables

    protected __processDataSourcePropertyValueChanged: boolean;

    // #endregion

    // #region Properties

    public dataSourceProperty!: PropertyString;
    public dataSourcePropertyType!: Property<BuiltInTypes>;
    public value!: PropertyObject;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<TPropertyValues>)
    {
        super(propertyValues);

        this.__processDataSourcePropertyValueChanged = true;
        this.addCssClass("rad-control-property-bound-control");

        // Trigger Property Changes

        this._suspendRefreshFromDataSource();
        this.dataSource.triggerChange();
        this.dataSourceProperty.triggerChange();
        this.dataSourcePropertyType.triggerChange();
        this._resumeRefreshFromDataSource();

        this.value.triggerChange();
    }

    // #endregion

    // #region Protected Methods

    protected _dataSourcePropertyChanged(change: PropertyChange<string>)
    {
        this.refreshFromDataSource();
    }
    protected _dataSourcePropertyTypeChanged(change: PropertyChange<BuiltInTypes>)
    {
        this.refreshFromDataSource();
    }
    protected _getDataSourceValue()
    {
        const
            dataSource = this.dataSource.get(),
            dataSourceProperty = this.dataSourceProperty.get();

        if (!dataSource || !dataSourceProperty)
            return;

        return dataSource.propertyValueByPathGet(dataSourceProperty);
    }
    protected _setDataSourceValue(value: any, inhibitEvent?: boolean)
    {
        const
            dataSource = this.dataSource.get(),
            dataSourceProperty = this.dataSourceProperty.get();

        if (!dataSource || !dataSourceProperty)
            return;

        dataSource.propertyValueByPathSet(dataSourceProperty, value, inhibitEvent);
    }
    protected _valueChanged(change: PropertyChange<any>)
    {
        this.triggerHandler("valueChanged", change);
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: TPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.dataSourceProperty = new PropertyString({
            context: this,
            initialValue: propertyValues.dataSourceProperty,
            changeHandler: this._dataSourcePropertyChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Data" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Data Source Property" }),
            ],
        });
        this.dataSourcePropertyType = new Property<BuiltInTypes>({
            context: this,
            dotNetType: "System.String, System.Private.CoreLib",
            initialValue: propertyValues.dataSourcePropertyType,
            changeHandler: this._dataSourcePropertyTypeChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Data" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Data Source Property Type" }),
            ],
        });
        this.value = new PropertyObject({
            context: this,
            initialValue: propertyValues.value,
            changeHandler: this._valueChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Data" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Value" }),
            ],
        });
    }

    protected override _appendToOrPrependTo(elementOrContainerControl: any[] | IContainerControl | HTMLElement | string, append: boolean, beforeSelector?: any)
    {
        super._appendToOrPrependTo(elementOrContainerControl, append, beforeSelector);
        this.dataSourceControl.set(this._getDataSourceControl());
    }
    protected override _inlineChanged(change: PropertyChange<boolean>)
    {
        super._inlineChanged(change);

        if (change.currentValue)
            this.removeCssClass("fluid");
        else
            this.addCssClass("fluid");
    }
    protected override _refreshFromDataSource()
    {
        super._refreshFromDataSource();

        if (!this.dataSource.get() || !this.dataSourceProperty.get())
            return;

        let
            value = this._getDataSourceValue();

        if (value === undefined)
        {
            value = null;
            this._setDataSourceValue(value, true);
        }

        this.value.set(value);
    }

    protected override onDataSourcePropertyValueChanged(evt: IJNativeEvent, change: DataSourcePropertyValueChange<any>)
    {
        const
            self = evt.data.self as this;

        if (!self.__processDataSourcePropertyValueChanged)
            return;

        const
            dataSourceProperty = self.dataSourceProperty.get();

        if (!dataSourceProperty)
            return;

        if (!(dataSourceProperty + ".").startsWith(change.path + "."))
            return;

        self.refreshFromDataSource();
    }

    // #endregion
}